/**
 * Shared helpers for computing Classic Rewards "points + cash" display pricing.
 *
 * These pure functions consolidate the reward pricing formula that was previously
 * duplicated across offer page tiles, search tiles, cart page, and checkout surfaces.
 */

import { FlightsClassTypes } from 'constants/flight'

export const ONE_POINT_PROMO_DATE = '2026-11-03'

// ---------------------------------------------------------------------------
// Preview reward rates for non-member / logged-out users
// ---------------------------------------------------------------------------

/**
 * Per-person hardcoded reward rates by base cabin, used to show indicative
 * pricing to users who haven't enrolled in Société (and therefore can't query
 * the real availability API). Displayed on the search calendar trip-options
 * table and the offer-page fare-class modal.
 */
/** Indicative reward rates per person for a return (round-trip) Maldives charter flight. */
export const REWARD_PREVIEW_RATE_BY_CABIN: Partial<Record<FlightsClassTypes, { pointsPerPerson: number; cashPerPerson: number }>> = {
  [FlightsClassTypes.ECONOMY]: { pointsPerPerson: 120_000, cashPerPerson: 199 },
  [FlightsClassTypes.LUXROW]: { pointsPerPerson: 180_000, cashPerPerson: 299 },
  [FlightsClassTypes.PREMIUM]: { pointsPerPerson: 200_000, cashPerPerson: 299 },
  [FlightsClassTypes.BUSINESS]: { pointsPerPerson: 500_000, cashPerPerson: 699 },
}

/** Indicative reward rates per person for a one-way Maldives charter flight (half of return rates). */
export const REWARD_PREVIEW_RATE_ONE_WAY_BY_CABIN: Partial<Record<FlightsClassTypes, { pointsPerPerson: number; cashPerPerson: number }>> = {
  [FlightsClassTypes.ECONOMY]: { pointsPerPerson: 60_000, cashPerPerson: 99.5 },
  [FlightsClassTypes.LUXROW]: { pointsPerPerson: 90_000, cashPerPerson: 149.5 },
  [FlightsClassTypes.PREMIUM]: { pointsPerPerson: 100_000, cashPerPerson: 149.5 },
  [FlightsClassTypes.BUSINESS]: { pointsPerPerson: 250_000, cashPerPerson: 349.5 },
}

const ONE_POINT_PROMO_REWARD_PREVIEW_RATE = { pointsPerPerson: 1, cashPerPerson: 100 }

export function getRewardPreviewRate({
  cabin,
  oneWayBundle = false,
  departureDate,
}: {
  cabin: FlightsClassTypes
  oneWayBundle?: boolean
  departureDate?: string
}): { pointsPerPerson: number; cashPerPerson: number } | undefined {
  if (cabin === FlightsClassTypes.ECONOMY && oneWayBundle && departureDate === ONE_POINT_PROMO_DATE) {
    return ONE_POINT_PROMO_REWARD_PREVIEW_RATE
  }

  return oneWayBundle ? REWARD_PREVIEW_RATE_ONE_WAY_BY_CABIN[cabin] : REWARD_PREVIEW_RATE_BY_CABIN[cabin]
}

/**
 * `journeyKey`s for specific campaign flights that are not meant to be sold as Classic Rewards
 * seats. The generic cabin-based preview rate above would still show a reward price for these
 * to non-member/logged-out users, so the Classic Rewards fare option is hidden entirely for
 * these journey keys instead.
 */
export const REWARD_PREVIEW_HIDDEN_JOURNEY_KEYS: ReadonlySet<string> = new Set([
  'sgmts=L0~957~MEL~03/11/2026-00:20~MLE~03/11/2026-05:55|c=economy|ff=economy',
  'sgmts=L0~957~MEL~03/11/2026-00:20~MLE~03/11/2026-05:55|c=premium_economy|ff=economy_plus',
  'sgmts=L0~957~MEL~03/11/2026-00:20~MLE~03/11/2026-05:55|c=business|ff=business',
])

/** Shared gate so every Classic Rewards card surface (modal, inline tile) hides consistently. */
export function isRewardHiddenForJourneyKey(journeyKey: string | undefined): boolean {
  return !!journeyKey && REWARD_PREVIEW_HIDDEN_JOURNEY_KEYS.has(journeyKey)
}

// ---------------------------------------------------------------------------
// Helper 1: Single reward bundle (hotel + reward flight)
// ---------------------------------------------------------------------------

export interface ComputeRewardBundlePriceArgs {
  /** Hotel-only price (regular). Must NOT include flight price. */
  hotelPrice: number
  /** Hotel-only price (LuxPlus member). Must NOT include flight price. */
  hotelMemberPrice?: number
  /** Total reward points across all seat-eligible passengers. */
  rewardPointsTotal: number
  /** Total flight cash co-pay across all seat-eligible passengers. */
  rewardCashCoPayTotal: number
}

export interface RewardBundlePriceResult {
  /** Total loyalty points required for the reward flight. */
  points: number
  /** Cash amount owed: hotel price + flight cash co-pay (regular pricing). */
  cash: number
  /** Cash amount owed with LuxPlus member pricing, or undefined if no member price. */
  memberCash?: number
}

/**
 * Computes the "points + cash" display price for a single Maldives reward package
 * (hotel + reward flight). Points cover the flight's points portion; cash covers the
 * hotel plus the flight's cash co-pay.
 *
 * @example
 * computeRewardBundlePrice({
 *   hotelPrice: 4500,
 *   hotelMemberPrice: 4200,
 *   rewardPointsTotal: 240000,
 *   rewardCashCoPayTotal: 700,
 * })
 * // => { points: 240000, cash: 5200, memberCash: 4900 }
 */
export function computeRewardBundlePrice({
  hotelPrice,
  hotelMemberPrice,
  rewardPointsTotal,
  rewardCashCoPayTotal,
}: ComputeRewardBundlePriceArgs): RewardBundlePriceResult {
  return {
    points: rewardPointsTotal,
    cash: hotelPrice + rewardCashCoPayTotal,
    memberCash: hotelMemberPrice !== undefined && hotelMemberPrice > 0
      ? hotelMemberPrice + rewardCashCoPayTotal
      : undefined,
  }
}

// ---------------------------------------------------------------------------
// Helper 2: Cart / checkout total with mixed items
// ---------------------------------------------------------------------------

export interface ComputeRewardCartTotalArgs {
  /** Full cart total (all items summed, before reward deduction). */
  totalCartPrice: number
  /** Total reward points across all seat-eligible passengers. */
  rewardPointsTotal: number
  /** Total flight cash co-pay across all seat-eligible passengers. */
  rewardCashCoPayTotal: number
  /**
   * Sum of the reward flight items' cash prices as they appear in `totalCartPrice`.
   * The function replaces this amount with `rewardCashCoPayTotal` to get the correct cash.
   * This avoids needing `totalFare` (which may be 0 in multi-cart).
   */
  rewardFlightsCashInTotal: number
}

export interface RewardCartTotalResult {
  /** Total loyalty points required. */
  points: number
  /** Cash portion of the cart total after deducting the points-covered flight amount. */
  cash: number
  /** The dollar value covered by points (= flightTotal - cashCoPay, clamped >= 0). */
  pointsCashPortion: number
}

/**
 * Computes the "points + cash" display total for a cart or checkout that contains
 * a Classic Rewards flight alongside other items (hotel, eSIM, etc.). The points-covered
 * portion of the flight is deducted from the full cart total so the customer sees only
 * the cash they actually pay.
 *
 * @example
 * computeRewardCartTotal({
 *   totalCartPrice: 9348,
 *   rewardPointsTotal: 240000,
 *   rewardCashCoPayTotal: 700,
 *   rewardFlightsCashInTotal: 2598,
 * })
 * // => { points: 240000, cash: 7450, pointsCashPortion: 1898 }
 */
export function computeRewardCartTotal({
  totalCartPrice,
  rewardPointsTotal,
  rewardCashCoPayTotal,
  rewardFlightsCashInTotal,
}: ComputeRewardCartTotalArgs): RewardCartTotalResult {
  const pointsCashPortion = Math.max(0, rewardFlightsCashInTotal - rewardCashCoPayTotal)
  return {
    points: rewardPointsTotal,
    cash: totalCartPrice - rewardFlightsCashInTotal + rewardCashCoPayTotal,
    pointsCashPortion,
  }
}

// ---------------------------------------------------------------------------
// Helper 3: Reward price display guard
// ---------------------------------------------------------------------------

/**
 * Determines whether reward pricing should be displayed. Returns a discriminated
 * result so callers can safely access the narrowed points/cash values.
 */
export function getRewardPriceDisplayState(opts: {
  rewardAvailable: boolean | undefined
  rewardSeatPointsTotal: number | undefined
  rewardSeatCashCoPayTotal: number | undefined
  rewardLockedForLogin?: boolean
}): { show: true; points: number; cash: number } | { show: false } {
  if (opts.rewardLockedForLogin) return { show: false }
  if (!opts.rewardAvailable || opts.rewardSeatPointsTotal == null || opts.rewardSeatCashCoPayTotal == null) return { show: false }
  return { show: true, points: opts.rewardSeatPointsTotal, cash: opts.rewardSeatCashCoPayTotal }
}
